/**
 * Case list hook (PAC-008/009).
 *
 * Wraps the role-scoped work-queue list surface owned by the Cases service:
 *   - GET /api/v1/queues/{role}?status=&stage=&...  → Page<Case>
 *
 * The `role` path segment drives RBAC; the returned list is the tenant-scoped
 * set of cases, ranked by priority. The hook re-fetches whenever the selected
 * role changes.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { Case, Page, Role } from '../api/types';

interface UseCasesResult {
  cases: Case[];
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

/** Loads the tenant's cases for a role-scoped queue. */
export function useCases(role: Role): UseCasesResult {
  const [cases, setCases] = useState<Case[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    apiClient
      .get<Page<Case>>(`/queues/${encodeURIComponent(role)}`)
      .then((page) => {
        setCases(page.items ?? []);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setCases([]);
      })
      .finally(() => setIsLoading(false));
  }, [role]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { cases, isLoading, error, refresh };
}
